import { BarChart2, BookOpen, Bot, HelpCircle, Layers, Link, Settings, Shield, TrendingUp, Zap } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const STEPS = [
  {
    id: 'broker',
    icon: Link,
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    title: 'Connect your broker',
    description: 'Sync trades automatically from Alpaca or Schwab. No manual entry needed.',
    action: { label: 'Go to Brokers', to: '/brokers' },
    checkFn: async () => {
      try {
        const { data } = await api.get('/broker-accounts');
        return data.success && data.data?.length > 0;
      } catch { return false; }
    },
  },
  {
    id: 'trade',
    icon: TrendingUp,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    title: 'Log your first trade',
    description: 'Add a trade manually if you haven\'t connected a broker yet.',
    action: { label: 'Go to Trades', to: '/trades' },
    checkFn: async () => {
      try {
        const { data } = await api.get('/trades?limit=1');
        return data.success && data.data?.trades?.length > 0;
      } catch { return false; }
    },
  },
  {
    id: 'journal',
    icon: BookOpen,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    title: 'Write a journal entry',
    description: 'Record your thoughts, mistakes, and lessons after each trading session.',
    action: { label: 'Go to Journal', to: '/journal' },
    checkFn: async () => {
      try {
        const { data } = await api.get('/journal?limit=1');
        return data.success && data.data?.entries?.length > 0;
      } catch { return false; }
    },
  },
  {
    id: 'analytics',
    icon: BarChart2,
    color: 'text-purple-400',
    bg: 'bg-purple-500/10',
    title: 'Review your analytics',
    description: 'After 10+ trades, your win rate, P&L curves, and performance edges appear.',
    action: { label: 'Go to Analytics', to: '/analytics' },
    checkFn: null,
  },
  {
    id: 'ai',
    icon: Bot,
    color: 'text-pink-400',
    bg: 'bg-pink-500/10',
    title: 'Set up AI coaching',
    description: 'Bring your own LLM key (or use a free Groq/Gemini key) for edge analysis and strategy sessions.',
    action: { label: 'Go to Strategy', to: '/strategy' },
    checkFn: null,
  },
];

const SHORTCUTS = [
  { keys: ['Shift', 'J'], description: 'Open Journal' },
  { keys: ['Shift', 'A'], description: 'Open Analytics' },
  { keys: ['Shift', 'B'], description: 'Open Brokers' },
];

const FAQ = [
  {
    q: 'How do I import trades from my broker?',
    a: 'Go to Brokers and connect your Alpaca or Schwab account. Trades sync automatically. You can also use Tools → Import to upload a CSV file.',
  },
  {
    q: 'Is my data private?',
    a: 'Yes. Your trade data is stored in a private PostgreSQL database. AI features use your own API key, which is encrypted with AES-256-GCM — never logged or sent to third parties.',
  },
  {
    q: 'What free AI options are available?',
    a: 'Google Gemini (aistudio.google.com) and Groq (console.groq.com) both offer free API tiers. Ollama and LM Studio let you run models entirely locally at no cost.',
  },
  {
    q: 'How does the weekly digest work?',
    a: 'Every Sunday at 8 AM UTC, you receive an email summarising your week: total P&L, win rate, top symbol, and trade count. You can opt out in Settings → Notifications.',
  },
  {
    q: 'How do I enable two-factor authentication?',
    a: 'Go to Settings → Security and click "Enable 2FA". Scan the QR code with Google Authenticator or Authy, then enter the 6-digit code to confirm.',
  },
  {
    q: 'What is a Playbook?',
    a: 'Playbooks are reusable trade setups you define: conditions, entry rules, risk parameters, and notes. Link trades to a playbook to track how well each strategy performs.',
  },
];

const HelpCenter = () => {
  const { user } = useAuth();
  const [stepStatus, setStepStatus] = useState({});
  const [openFaq, setOpenFaq] = useState(null);

  const checkSteps = useCallback(async () => {
    const results = {};
    await Promise.all(
      STEPS.filter(s => s.checkFn).map(async (step) => {
        results[step.id] = await step.checkFn();
      })
    );
    setStepStatus(results);
  }, []);

  useEffect(() => { checkSteps(); }, [checkSteps]);

  const completedCount = Object.values(stepStatus).filter(Boolean).length;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <HelpCircle size={24} className="text-slate-400" />
        <div>
          <h1 className="text-2xl font-bold text-slate-200">Help & Getting Started</h1>
          <p className="text-sm text-slate-400">
            Hi {user?.firstName || 'there'} — here's what to do next.
          </p>
        </div>
      </div>

      {/* Setup Checklist */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-slate-200 font-semibold">Setup Checklist</h2>
          <span className="text-xs text-slate-400">
            {completedCount}/{STEPS.filter(s => s.checkFn).length} done
          </span>
        </div>
        <div className="space-y-3">
          {STEPS.map((step) => {
            const done = stepStatus[step.id];
            const Icon = step.icon;
            return (
              <div
                key={step.id}
                className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                  done
                    ? 'border-emerald-500/20 bg-emerald-500/5'
                    : 'border-slate-700/50 bg-slate-700/20'
                }`}
              >
                <div className={`w-9 h-9 rounded-lg ${step.bg} flex items-center justify-center shrink-0`}>
                  {done
                    ? <span className="text-emerald-400 font-bold text-sm">✓</span>
                    : <Icon size={18} className={step.color} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${done ? 'text-slate-400 line-through' : 'text-slate-200'}`}>
                    {step.title}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">{step.description}</p>
                </div>
                {!done && step.action && (
                  <RouterLink
                    to={step.action.to}
                    className="shrink-0 text-xs px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors whitespace-nowrap"
                  >
                    {step.action.label}
                  </RouterLink>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Keyboard Shortcuts */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Zap size={16} className="text-amber-400" />
          <h2 className="text-slate-200 font-semibold">Keyboard Shortcuts</h2>
        </div>
        <div className="space-y-2">
          {SHORTCUTS.map(({ keys, description }) => (
            <div key={description} className="flex items-center justify-between">
              <span className="text-sm text-slate-400">{description}</span>
              <div className="flex items-center gap-1">
                {keys.map((k, i) => (
                  <span key={i}>
                    <kbd className="px-2 py-0.5 text-xs bg-slate-700 border border-slate-600 rounded text-slate-300 font-mono">
                      {k}
                    </kbd>
                    {i < keys.length - 1 && <span className="text-slate-600 mx-1">+</span>}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 gap-3">
        <RouterLink
          to="/settings"
          className="flex items-center gap-3 p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:bg-slate-800 transition-colors"
        >
          <Settings size={18} className="text-slate-400" />
          <div>
            <p className="text-sm font-medium text-slate-200">Settings</p>
            <p className="text-xs text-slate-500">2FA, notifications</p>
          </div>
        </RouterLink>
        <RouterLink
          to="/strategy"
          className="flex items-center gap-3 p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:bg-slate-800 transition-colors"
        >
          <Bot size={18} className="text-slate-400" />
          <div>
            <p className="text-sm font-medium text-slate-200">AI Strategy</p>
            <p className="text-xs text-slate-500">Edge analysis, chat</p>
          </div>
        </RouterLink>
        <RouterLink
          to="/tools/import"
          className="flex items-center gap-3 p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:bg-slate-800 transition-colors"
        >
          <Layers size={18} className="text-slate-400" />
          <div>
            <p className="text-sm font-medium text-slate-200">Import Trades</p>
            <p className="text-xs text-slate-500">CSV upload</p>
          </div>
        </RouterLink>
        <RouterLink
          to="/playbooks"
          className="flex items-center gap-3 p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:bg-slate-800 transition-colors"
        >
          <BookOpen size={18} className="text-slate-400" />
          <div>
            <p className="text-sm font-medium text-slate-200">Playbooks</p>
            <p className="text-xs text-slate-500">Strategy templates</p>
          </div>
        </RouterLink>
        <RouterLink
          to="/settings"
          className="flex items-center gap-3 p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:bg-slate-800 transition-colors"
        >
          <Shield size={18} className="text-slate-400" />
          <div>
            <p className="text-sm font-medium text-slate-200">Enable 2FA</p>
            <p className="text-xs text-slate-500">Protect your account</p>
          </div>
        </RouterLink>
        <a
          href="mailto:support@rmultiverse.com"
          className="flex items-center gap-3 p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:bg-slate-800 transition-colors"
        >
          <HelpCircle size={18} className="text-slate-400" />
          <div>
            <p className="text-sm font-medium text-slate-200">Report an Issue</p>
            <p className="text-xs text-slate-500">support@rmultiverse.com</p>
          </div>
        </a>
      </div>

      {/* FAQ */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <h2 className="text-slate-200 font-semibold mb-4">Frequently Asked Questions</h2>
        <div className="space-y-2">
          {FAQ.map((item, i) => (
            <div key={i} className="border border-slate-700/50 rounded-xl overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-700/20 transition-colors"
              >
                <span className="text-sm font-medium text-slate-200">{item.q}</span>
                <span className="text-slate-500 ml-2 shrink-0">
                  {openFaq === i ? '−' : '+'}
                </span>
              </button>
              {openFaq === i && (
                <div className="px-4 pb-4">
                  <p className="text-sm text-slate-400 leading-relaxed">{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Contact */}
      <div className="bg-emerald-500/8 border border-emerald-500/20 rounded-xl p-6 text-center">
        <p className="text-slate-300 text-sm font-medium mb-1">Still stuck? We're here.</p>
        <p className="text-slate-500 text-xs mb-3">Response within 24 hours on business days.</p>
        <a
          href="mailto:support@rmultiverse.com"
          className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm font-medium transition-colors"
        >
          support@rmultiverse.com
        </a>
      </div>
    </div>
  );
};

export default HelpCenter;
